import React, { useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { useMutation } from 'react-query';
import { KibSpinner } from '@chewy/kib-interstitials-react';
import { KibButtonNew } from '@chewy/kib-controls-react';
import { KibIngressCard } from '@chewy/kib-cards-react';
import PageLoader from '../../components/loaders/PageLoader';
import { uploadImage, updatePetProfile } from '../../api/petApi';
import { PetInterface } from '../../types/pet';
import FetchError from '../../types/http';
import AnalyticsContext from '../../context/AnalyticsContext';
import ImageUpload from '../image-upload/ImageUpload';
import log from '../../util/log';
import getImageUrl from '../../util/getImageUrl';
import isFormattedImage from '../../util/isFormattedImage';
import { handleAuthError } from '../../util/auth';
import { getPetPossessivePronouns } from '../../util/getPetPronouns';
import {
  ApiPetProfile,
  ApiPetProfileAvatarImageSourceEnum,
} from '../../api/generated-client/PetProfile-Service-A.v1';
import './memorial-content.scss';
import {
  getAvatarDownloadUrl,
  getBannerDownloadUrl,
  getBannerUrlWithScheme,
} from './utils/memorial-detail-helper';

export default function MemorialContent({
  petProfile,
  avatarUrl,
  setAvatarUrl,
  avatarSrc,
  setAvatarSrc,
  setOpenDetails,
  setShowDownloadSheet,
  setSelectedPhotoUrl,
}: {
  petProfile: PetInterface;
  avatarUrl?: string;
  setAvatarUrl: React.Dispatch<React.SetStateAction<string | undefined>>;
  avatarSrc?: string;
  setAvatarSrc: React.Dispatch<
    React.SetStateAction<ApiPetProfileAvatarImageSourceEnum | undefined>
  >;
  setOpenDetails: React.Dispatch<React.SetStateAction<boolean>>;
  setShowDownloadSheet: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedPhotoUrl: React.Dispatch<React.SetStateAction<string>>;
}) {
  const location = useLocation();
  const analytics = useContext(AnalyticsContext);

  const [isLoading, setIsLoading] = useState(true);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [dismissedUpload, setDismissedUpload] = useState(false);
  const [hasPhotos, setHasPhotos] = useState(
    avatarSrc === ApiPetProfileAvatarImageSourceEnum.Photo || !!petProfile.photoUrl,
  );

  const pronoun = getPetPossessivePronouns(petProfile.gender);
  const photosTitle = `${pronoun} Photos`;
  const addPhotoTitle = 'Would you like to add a photo?';
  const addPhotoSubtitle = `Keep one of your favorite photos on ${petProfile.name}'s memorialized profile.`;
  const detailsTitle = `${pronoun} Details`;
  const detailsSubtitle = `Look back at ${pronoun.toLowerCase()} Chewy profile`;
  const dismissPhotoCopy = `I'd rather not add a photo`;
  const uploadText = 'Upload';

  const getImage = (image: string) =>
    isFormattedImage(image) ? image : getImageUrl(image, 125, 'SS');

  const modifiedAvatarUrl = avatarUrl?.replace(/(_SS\d+_)/, '_SS550_');
  const modifiedBannerUrl = getBannerUrlWithScheme(
    petProfile.photoUrl?.replace(/_SS150_/, '_QL100_'),
  );

  const transformPetToApiPetProfile = (pet: PetInterface): ApiPetProfile => ({
    name: pet.name,
    type: {
      id: Number.parseInt(pet.petTypeId),
      name: pet.petType.name,
    },
  });

  const updatePet = useMutation(
    (payload: ApiPetProfile) => updatePetProfile(Number(petProfile.id), payload),
    {
      onSuccess: () => {
        // TODO Analytics to show custom pic has been added
      },
      onError: (error: FetchError) => {
        log.error('Failed to Update Pet Profile:', `Status code: ${error.status}`, error);
        handleAuthError(error.status, location.pathname); // TODO check this
      },
    },
  );

  useEffect(() => {
    // Check Local Storage for the 'dismissedUpload' flag
    const isDismissedInStorage = localStorage.getItem(`dismissedUpload-${petProfile.id}`);

    if (isDismissedInStorage === 'true') {
      setDismissedUpload(true);
    } else setDismissedUpload(false);

    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateProfileAvatar = (data: string) => {
    updatePet.mutate({
      ...transformPetToApiPetProfile(petProfile!),
      avatar: {
        id: 0,
        imageUri: data,
        imageSource: ApiPetProfileAvatarImageSourceEnum.Photo,
      },
    });
  };

  const imageMutation = useMutation(uploadImage, {
    onSuccess: (data) => {
      updateProfileAvatar(data);
      setIsImageLoading(false);
      setHasPhotos(true);
      setAvatarUrl(getImage(data));
      setAvatarSrc(ApiPetProfileAvatarImageSourceEnum.Photo);
    },
  });

  const handleOpenDetails = () => {
    setOpenDetails(true);
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_details',
    });
  };

  const handleAvatarClick = () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_avatar_photo',
    });
    setShowDownloadSheet(true);
    const avatarDownloadUrl = getAvatarDownloadUrl(avatarUrl);
    avatarDownloadUrl && setSelectedPhotoUrl(avatarDownloadUrl);
  };

  const handleBannerClick = () => {
    analytics.trackRainbow({
      name: 'pet profile memorial - clicked',
      category: 'pet-profile-memorial',
      action: 'click',
      label: 'pet_profile_memorial_cover_photo',
    });
    setShowDownloadSheet(true);
    const bannerUrl = getBannerDownloadUrl(petProfile?.photoUrl);
    const bannerUrlWithScheme = getBannerUrlWithScheme(bannerUrl);
    bannerUrlWithScheme && setSelectedPhotoUrl(bannerUrlWithScheme);
  };

  const handleUploadCustomAvatar = (selectedImage: Blob) => {
    setIsImageLoading(true);
    imageMutation.mutate({ body: selectedImage });
  };

  const handleDismiss = () => {
    setDismissedUpload(true);
    // Set a flag in Local Storage
    localStorage.setItem(`dismissedUpload-${petProfile.id}`, 'true');
  };

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isLoading ? (
        <PageLoader
          className="memorial-content__loading memorial-content__fade"
          data-testid="memorial-content-loader"
        />
      ) : (
        <div className="memorial-content memorial-content__fade" data-testid="memorial-content">
          {isImageLoading && !hasPhotos ? (
            <KibSpinner />
          ) : hasPhotos ? (
            <div
              className="memorial-content__photos"
              data-testid="memorial-content-photos-container"
            >
              <div
                className="memorial-content__photos-title"
                data-testid="memorial-content-photos-title"
              >
                {photosTitle}
              </div>
              {avatarSrc === ApiPetProfileAvatarImageSourceEnum.Photo && avatarUrl && (
                <button
                  onClick={handleAvatarClick}
                  data-testid="memorial-content-avatar-photo"
                  className="memorial-content__photos--button"
                  style={{
                    backgroundImage: `url(${modifiedAvatarUrl})`,
                  }}
                />
              )}
              {petProfile.photoUrl && (
                <button
                  onClick={handleBannerClick}
                  data-testid="memorial-content-cover-photo"
                  className="memorial-content__photos--button"
                  style={{
                    backgroundImage: `url(${modifiedBannerUrl})`,
                  }}
                />
              )}
            </div>
          ) : (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
              {!dismissedUpload && (
                <div
                  className="memorial-content__photos memorial-content__photos--dashed"
                  data-testid="memorial-content-no-photos-container"
                >
                  <div
                    className="memorial-content__noPhotos--title"
                    data-testid="memorial-content-no-photos-title"
                  >
                    {addPhotoTitle}
                  </div>
                  <div
                    className="memorial-content__noPhotos--subtitle"
                    data-testid="memorial-content-no-photos-subtitle"
                  >
                    {addPhotoSubtitle}
                  </div>
                  <div
                    className="memorial-content__noPhotos--options"
                    data-testid="memorial-content-no-photos-options"
                  >
                    <ImageUpload
                      onCompleteCrop={handleUploadCustomAvatar}
                      title="Upload Avatar Image"
                    >
                      <KibButtonNew
                        emphasis="tertiary"
                        size="medium"
                        data-testid="memorial-content-no-photos-upload-btn"
                        className="memorial-content__noPhotos--button"
                      >
                        {uploadText}
                      </KibButtonNew>
                    </ImageUpload>
                    <button
                      className="memorial-content__noPhotos--dismissContainer"
                      data-testid="memorial-content-no-photos-dismiss-btn"
                      onClick={handleDismiss}
                    >
                      <div className="memorial-content__noPhotos--dismissText">
                        {dismissPhotoCopy}
                      </div>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          <KibIngressCard
            data-testid="memorial-content-details"
            subtitle={detailsSubtitle}
            title={detailsTitle}
            onClick={handleOpenDetails}
            className="memorial-content__details"
          />
        </div>
      )}
    </>
  );
}
